import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Grid3x3, List, Loader2, Users, FileEdit, Theater, GraduationCap, ClipboardCheck, Activity, Globe, Building2, Tags, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AgentCard } from '@/components/admin/AgentCard';
import { AgentTable } from '@/components/admin/AgentTable';
import { DraftCard } from '@/components/admin/DraftCard';
import { AgentCategoryManager } from '@/components/admin/AgentCategoryManager';
import { ArchivedAgentsPanel } from '@/components/admin/ArchivedAgentsPanel';
import { fetchAgents, fetchAgentStats } from '@/lib/api';
import { fetchAgentCategoryCatalogue } from '@/lib/agentCategories';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { toast } from 'sonner';
import { MASTER_TENANT_ID } from '@/types/tenant';
import { useOrganisation, type OrganisationAssignment } from '@/contexts/OrganisationContext';
import {
  fetchAgentBranchAssignments,
  fetchEnterpriseBranches,
  type AgentBranchAssignment,
  type AgentBranchOption,
} from '@/lib/api/agentBranchAccess';

type AgentCategory = 'all' | 'roleplays' | 'coaches' | 'examiners';
type AgentSubType = 'all' | string;

const AGENT_CATEGORY_CONFIG: Record<AgentCategory, {
  label: string;
  icon: React.ReactNode;
  apiType: string; // value sent to API (comma-separated for multi-type)
  subTypes: { value: string; label: string }[];
  filterField: 'type' | 'domain'; // what field the subType filters on
}> = {
  all: { label: 'All Agents', icon: <Activity size={16} />, apiType: 'all', subTypes: [], filterField: 'type' },
  roleplays: {
    label: 'Roleplays',
    icon: <Theater size={16} />,
    apiType: 'patient,customer',
    filterField: 'type',
    subTypes: [
      { value: 'all', label: 'All Roleplays' },
      { value: 'patient', label: 'Patient' },
      { value: 'customer', label: 'Customer' },
    ],
  },
  coaches: {
    label: 'Coaches',
    icon: <GraduationCap size={16} />,
    apiType: 'coach',
    filterField: 'domain',
    subTypes: [
      { value: 'all', label: 'All Coaches' },
      { value: 'clinical', label: 'Clinical' },
      { value: 'business', label: 'Business' },
      { value: 'sales', label: 'Sales' },
      { value: 'hr', label: 'Personnel/HR' },
      { value: 'presentation', label: 'Presentation' },
    ],
  },
  examiners: {
    label: 'Examiners',
    icon: <ClipboardCheck size={16} />,
    apiType: 'examiner',
    filterField: 'domain',
    subTypes: [
      { value: 'all', label: 'All Examiners' },
      { value: 'clinical', label: 'Clinical' },
      { value: 'technical', label: 'Technical' },
      { value: 'general', label: 'General' },
    ],
  },
};
interface Draft {
  id: string;
  name: string;
  agent_type: string;
  platform: string;
  current_step: number;
  updated_at: string;
  expires_at: string;
  form_data?: {
    isTemplateMode?: boolean;
  } | null;
}

export interface SourceAgentHistory {
  id: string;
  name: string;
  completions: number;
  avg_score: number | null;
  status: string;
}

async function attachSourceAgentHistory<
  T extends { source_agent_id?: string | null },
>(agents: T[]): Promise<Array<T & { source_agent_history?: SourceAgentHistory }>> {
  const sourceIds = [...new Set(
    agents.map((agent) => agent.source_agent_id).filter((id): id is string => Boolean(id)),
  )];
  if (sourceIds.length === 0) return agents;

  const { data, error } = await supabase
    .from('simulation_agents')
    .select('id, name, completions, avg_score, status')
    .in('id', sourceIds);

  if (error) {
    console.warn('Unable to load retained source-agent history:', error);
    return agents;
  }

  const historyBySourceId = new Map(
    ((data || []) as SourceAgentHistory[]).map((history) => [history.id, history]),
  );

  return agents.map((agent) => {
    const history = agent.source_agent_id
      ? historyBySourceId.get(agent.source_agent_id)
      : undefined;
    return history ? { ...agent, source_agent_history: history } : agent;
  });
}

async function attachAverageScores<T extends { id: string; avg_score?: number | null }>(agents: T[]): Promise<T[]> {
  const agentIds = [...new Set(agents.map((agent) => agent.id).filter(Boolean))];
  if (agentIds.length === 0) return agents;

  const [byAgent, bySimulation] = await Promise.all([
    supabase
      .from('simulation_evaluations')
      .select('id, agent_id, simulation_id, overall_score')
      .in('agent_id', agentIds)
      .not('overall_score', 'is', null),
    supabase
      .from('simulation_evaluations')
      .select('id, agent_id, simulation_id, overall_score')
      .in('simulation_id', agentIds)
      .not('overall_score', 'is', null),
  ]);

  if (byAgent.error || bySimulation.error) {
    console.warn('Unable to calculate agent average scores:', byAgent.error || bySimulation.error);
    return agents;
  }

  const evaluationById = new Map<string, any>();
  [...(byAgent.data || []), ...(bySimulation.data || [])].forEach((evaluation: any) => {
    evaluationById.set(evaluation.id, evaluation);
  });

  const scoresByAgent = new Map<string, number[]>();
  evaluationById.forEach((evaluation) => {
    const agentId = evaluation.agent_id || evaluation.simulation_id;
    const score = Number(evaluation.overall_score);
    if (!agentId || !Number.isFinite(score) || !agentIds.includes(agentId)) return;
    const scores = scoresByAgent.get(agentId) || [];
    scores.push(score);
    scoresByAgent.set(agentId, scores);
  });

  return agents.map((agent) => {
    const scores = scoresByAgent.get(agent.id);
    if (!scores?.length) return agent;
    return {
      ...agent,
      avg_score: scores.reduce((sum, score) => sum + score, 0) / scores.length,
    };
  });
}

async function attachEnterpriseBranchAccess<T extends { id: string }>(
  agents: T[],
  tenantId: string,
): Promise<Array<T & {
  enterprise_branch_ids: string[];
  enterprise_branch_names: string[];
  enterprise_scope_assignments: AgentBranchAssignment[];
  enterprise_branch_access_loaded: true;
}>> {
  const assignments = await fetchAgentBranchAssignments(
    tenantId,
    agents.map((agent) => agent.id),
  );
  const assignmentsByAgent = new Map<string, Array<{ branch_id: string; branch_name: string }>>();

  assignments.forEach((assignment) => {
    const rows = assignmentsByAgent.get(assignment.agent_id) || [];
    rows.push({ branch_id: assignment.branch_id, branch_name: assignment.branch_name });
    assignmentsByAgent.set(assignment.agent_id, rows);
  });

  return agents.map((agent) => {
    const rows = (assignmentsByAgent.get(agent.id) || [])
      .filter((row, index, candidates) => (
        candidates.findIndex((candidate) => candidate.branch_id === row.branch_id) === index
      ))
      .sort((left, right) => left.branch_name.localeCompare(right.branch_name));
    return {
      ...agent,
      enterprise_branch_ids: rows.map((row) => row.branch_id),
      enterprise_branch_names: rows.map((row) => row.branch_name),
      enterprise_scope_assignments: assignments.filter((assignment) => assignment.agent_id === agent.id),
      enterprise_branch_access_loaded: true,
    };
  });
}

function canManageAgentInEnterpriseScope(
  scopeAssignments: readonly AgentBranchAssignment[],
  roleAssignments: readonly OrganisationAssignment[],
): boolean {
  const activeRoleAssignments = roleAssignments.filter((assignment) => assignment.status === 'active');
  if (activeRoleAssignments.some((assignment) => assignment.role === 'company_admin')) return true;

  return scopeAssignments.some((scope) => activeRoleAssignments.some((assignment) => {
    if (assignment.role === 'branch_admin') {
      return assignment.branch_id === scope.branch_id;
    }
    if (assignment.role === 'unit_admin' || assignment.role === 'unit_manager') {
      return assignment.branch_id === scope.branch_id
        && assignment.unit_id !== null
        && assignment.unit_id === scope.unit_id;
    }
    return false;
  }));
}

export function AgentManagement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'agents' | 'drafts' | 'categories' | 'archived'>('agents');
  const [dataRevision, setDataRevision] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [agentCategory, setAgentCategory] = useState<AgentCategory>('all');
  const [agentSubType, setAgentSubType] = useState<AgentSubType>('all');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [agentSource, setAgentSource] = useState<'all' | 'system' | 'company'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<any[]>([]);
  const [systemAgents, setSystemAgents] = useState<any[]>([]);
  const [companyAgents, setCompanyAgents] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string; tenant_id?: string | null }>>([]);
  const [enterpriseBranches, setEnterpriseBranches] = useState<AgentBranchOption[]>([]);
  const [allTenants, setAllTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [userCompany, setUserCompany] = useState<{ id: string; name: string } | null>(null);
  const [stats, setStats] = useState({ total: 0, patient: 0, customer: 0, coach: 0, examiner: 0, roleplays: 0, activeSimulations: 0 });

  const { hasRole, user } = useAuth();
  const { tenant, loading: tenantLoading, isMasterTenant } = useTenant();
  const {
    assignments: organisationAssignments,
    activeAssignment,
    hasEnterpriseHierarchy,
    canCreateAssets,
    isCompanyAdmin,
    isBranchAdmin,
  } = useOrganisation();
  const isAdmin = hasRole('admin');
  const categoryCatalogueQuery = useQuery({
    queryKey: ['agent-category-catalogue'],
    queryFn: fetchAgentCategoryCatalogue,
  });
  
  // Use tenant from URL, not user's profile tenant
  const currentTenantId = tenant.tenant_id;
  
  // Check if current tenant is a white-label tenant (not master MEDD-SIM tenant)
  const isWhiteLabelTenant = currentTenantId && currentTenantId !== MASTER_TENANT_ID;

  // Load companies for master tenant admin filter AND user's company for white-label tenants
  useEffect(() => {
    // Master tenant admins need to filter across every tenant/company on the MEDD platform.
    // White-label tenant users still get their tenant-safe scoped controls below.
    if (isAdmin && !isWhiteLabelTenant) {
      supabase
        .from('companies')
        .select('id, name, tenant_id')
        .eq('is_active', true)
        .order('name')
        .then(({ data }) => setCompanies(data || []));
    }
    
    // Load user's company for white-label tenant users
    if (isWhiteLabelTenant && user?.company_id) {
      supabase
        .from('companies')
        .select('id, name')
        .eq('id', user.company_id)
        .single()
        .then(({ data }) => setUserCompany(data || null));
    }
    
    // Load all tenants for badge display
    supabase
      .from('tenants')
      .select('id, name')
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => setAllTenants(data || []));
  }, [isAdmin, isWhiteLabelTenant, user?.company_id]);

  useEffect(() => {
    if (!hasEnterpriseHierarchy || !currentTenantId) {
      setEnterpriseBranches([]);
      setFilterBranch('all');
      return;
    }

    fetchEnterpriseBranches(currentTenantId)
      .then(setEnterpriseBranches)
      .catch((error) => console.error('Failed to load agent branch filters:', error));
  }, [currentTenantId, hasEnterpriseHierarchy]);

  // Load drafts
  const loadDrafts = async () => {
    setDraftsLoading(true);
    try {
      const { data, error } = await supabase
        .from('agent_drafts')
        .select('id, name, agent_type, platform, current_step, updated_at, expires_at, form_data')
        .gt('expires_at', new Date().toISOString())
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      setDrafts(((data || []) as Draft[]).filter(draft => !draft.form_data?.isTemplateMode));
    } catch (error) {
      console.error('Failed to load drafts:', error);
    } finally {
      setDraftsLoading(false);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  const handleDeleteDraft = async (draftId: string) => {
    try {
      const { error } = await supabase
        .from('agent_drafts')
        .delete()
        .eq('id', draftId);
      
      if (error) throw error;
      
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Draft deleted');
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  // Compute the effective API type from category + subType
  const effectiveFilterType = useMemo(() => {
    if (agentCategory === 'all') return 'all';
    const config = AGENT_CATEGORY_CONFIG[agentCategory];
    // If a specific sub-type within roleplays is selected, use that instead
    if (agentCategory === 'roleplays' && agentSubType !== 'all') {
      return agentSubType; // 'patient' or 'customer'
    }
    return config.apiType;
  }, [agentCategory, agentSubType]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        if (isWhiteLabelTenant) {
          // White-label tenant view: fetch both system agents and company agents separately
          const [sysAgents, compAgents, statsData] = await Promise.all([
            // System agents (global agents with no company)
            fetchAgents({ 
              type: effectiveFilterType, 
              specialty: filterSpecialty,
              company_id: 'global'
            }),
            // Tenant agents - ALL non-system agents for this tenant (not filtered by user's company)
            fetchAgents({ 
              type: effectiveFilterType, 
              specialty: filterSpecialty,
              tenant_id: currentTenantId  // Get ALL agents for this tenant
            }),
            fetchAgentStats({ 
              tenant_id: currentTenantId, 
              company_id: user?.company_id 
            }),
          ]);
          
          const [scoredSystemAgents, scoredTenantAgents, tenantHistoryAgents] = await Promise.all([
            attachAverageScores(sysAgents),
            attachAverageScores(compAgents),
            attachSourceAgentHistory(compAgents),
          ]);
          const sourceHistoryByAgentId = new Map(
            tenantHistoryAgents
              .filter((agent) => agent.source_agent_history)
              .map((agent) => [agent.id, agent.source_agent_history]),
          );
          const tenantAgentsWithSourceHistory = scoredTenantAgents.map((agent) => {
            const sourceHistory = sourceHistoryByAgentId.get(agent.id);
            return sourceHistory ? { ...agent, source_agent_history: sourceHistory } : agent;
          });
          const scoredCompanyAgents = hasEnterpriseHierarchy && currentTenantId
            ? await attachEnterpriseBranchAccess(tenantAgentsWithSourceHistory, currentTenantId)
            : tenantAgentsWithSourceHistory;

          setSystemAgents(scoredSystemAgents);
          setCompanyAgents(scoredCompanyAgents);
          
          // Combine based on selected source filter
          if (agentSource === 'system') {
            setAgents(scoredSystemAgents);
          } else if (agentSource === 'company') {
            setAgents(scoredCompanyAgents);
          } else {
            // Combine both, avoiding duplicates
            const allAgentIds = new Set<string>();
            const combined: any[] = [];
            [...scoredSystemAgents, ...scoredCompanyAgents].forEach(agent => {
              if (!allAgentIds.has(agent.id)) {
                allAgentIds.add(agent.id);
                combined.push(agent);
              }
            });
            setAgents(combined);
          }
          
          setStats(statsData);
        } else {
          // Master tenant view: use existing filter logic
          const selectedTenantId = filterCompany.startsWith('tenant:')
            ? filterCompany.replace('tenant:', '')
            : undefined;
          const selectedCompanyId = filterCompany !== 'all' && filterCompany !== 'global' && !selectedTenantId
            ? filterCompany
            : undefined;

          const [agentsData, statsData] = await Promise.all([
            fetchAgents({ 
              type: effectiveFilterType, 
              specialty: filterSpecialty,
              tenant_id: selectedTenantId,
              company_id: filterCompany === 'global' ? 'global' : selectedCompanyId
            }),
            fetchAgentStats(),
          ]);
          setAgents(await attachAverageScores(agentsData));
          setStats(statsData);
        }
      } catch (error) {
        console.error('Failed to load agents:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [effectiveFilterType, filterSpecialty, filterCompany, agentSource, isWhiteLabelTenant, user?.company_id, currentTenantId, dataRevision, hasEnterpriseHierarchy]);

  // Client-side sub-filtering by domain (coaches and examiners) or type (roleplays)
  // Also add is_system flag for UI badges
  const filteredAgents = useMemo(() => {
    const companyNameById = new Map(companies.map(company => [company.id, company.name]));
    if (userCompany) companyNameById.set(userCompany.id, userCompany.name);
    const tenantNameById = new Map(allTenants.map(tenant => [tenant.id, tenant.name]));
    const searchTerms = searchQuery
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    let result = agents.map(agent => {
      const isSystemAgent = agent.is_system === true;
      const scopeAssignments = (agent.enterprise_scope_assignments || []) as AgentBranchAssignment[];
      const canManageAgent = !hasEnterpriseHierarchy
        || isMasterTenant
        || (!isSystemAgent && canManageAgentInEnterpriseScope(scopeAssignments, organisationAssignments));
      const duplicateScopeLabel = activeAssignment?.unit_name
        ? `${activeAssignment.branch_name} → ${activeAssignment.unit_name}`
        : activeAssignment?.branch_name || 'your organisation scope';

      return {
        ...agent,
        is_system: isSystemAgent,
        enterprise_can_manage: canManageAgent,
        enterprise_can_duplicate: hasEnterpriseHierarchy && canCreateAssets && !canManageAgent,
        enterprise_duplicate_scope_label: duplicateScopeLabel,
      };
    });
    
    if (agentSubType !== 'all') {
      const config = AGENT_CATEGORY_CONFIG[agentCategory];
      if (config.filterField === 'type') {
        // Filter by agent type (patient, customer)
        result = result.filter((a: any) => a.type === agentSubType);
      } else if (config.filterField === 'domain') {
        // Filter by domain (clinical, business, sales, etc.)
        result = result.filter((a: any) => a.domain === agentSubType);
      }
    }

    if (filterCategory !== 'all') {
      result = result.filter((agent: any) => (agent.agent_category || 'uncategorised') === filterCategory);
    }

    if (hasEnterpriseHierarchy && filterBranch !== 'all') {
      result = result.filter((agent: any) => {
        if (agent.is_system) return filterBranch !== 'all-branches';
        const branchIds = agent.enterprise_branch_ids || [];
        if (filterBranch === 'all-branches') return branchIds.length === 0;
        return branchIds.length === 0 || branchIds.includes(filterBranch);
      });
    }

    if (searchTerms.length > 0) {
      result = result.filter((agent: any) => {
        const companyName = agent.company_id ? companyNameById.get(agent.company_id) : '';
        const tenantName = agent.tenant_id ? tenantNameById.get(agent.tenant_id) : '';
        const searchable = [
          agent.name,
          agent.title,
          agent.occupation,
          agent.specialty,
          agent.clinical_specialty,
          agent.type,
          agent.domain,
          agent.industry,
          agent.agent_category,
          agent.disease_id,
          agent.roleplay_focus,
          agent.brief_description,
          agent.company_organization,
          agent.prospect_role,
          agent.scenario_context,
          agent.presenting_complaint,
          ...(agent.enterprise_branch_names || []),
          (agent.enterprise_branch_names || []).length === 0 && !agent.is_system ? 'all branches organisation wide' : '',
          companyName,
          tenantName,
          agent.is_system ? 'system global' : '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchTerms.every(term => searchable.includes(term));
      });
    }

    if (hasEnterpriseHierarchy) {
      result.sort((left: any, right: any) => {
        const leftScope = left.is_system
          ? 'System'
          : left.enterprise_branch_names?.join(', ') || 'All branches';
        const rightScope = right.is_system
          ? 'System'
          : right.enterprise_branch_names?.join(', ') || 'All branches';
        return leftScope.localeCompare(rightScope) || left.name.localeCompare(right.name);
      });
    }

    return result;
  }, [
    activeAssignment?.branch_name,
    activeAssignment?.unit_name,
    agents,
    agentCategory,
    agentSubType,
    allTenants,
    canCreateAssets,
    companies,
    filterBranch,
    filterCategory,
    hasEnterpriseHierarchy,
    isMasterTenant,
    organisationAssignments,
    searchQuery,
    userCompany,
  ]);

  const categoryOptions = useMemo(() => {
    const labels = new Set<string>();
    categoryCatalogueQuery.data?.categories.forEach((category) => labels.add(category.name));
    agents.forEach((agent: any) => {
      const label = (agent.agent_category || '').trim();
      if (label) labels.add(label);
    });
    return Array.from(labels).sort((a, b) => a.localeCompare(b));
  }, [agents, categoryCatalogueQuery.data]);

  const formatCompanyFilterLabel = (company: { name: string; tenant_id?: string | null }) => {
    const tenantName = company.tenant_id ? allTenants.find(t => t.id === company.tenant_id)?.name : null;
    if (!tenantName || tenantName === company.name) return company.name;
    return `${company.name} (${tenantName})`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2">Agents</h1>
            <p className="text-lg text-muted-foreground">
              Manage simulation agents for patient consultations and customer interactions
            </p>
          </div>
          {activeTab !== 'categories' && activeTab !== 'archived' && (
            <Button onClick={() => navigate('/admin/agents/create')} size="lg">
              <Plus size={20} />
              Create Agent
            </Button>
          )}
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'agents' | 'drafts' | 'categories' | 'archived')} className="mb-6">
          <TabsList className={`grid w-full ${hasEnterpriseHierarchy && canCreateAssets ? 'max-w-3xl grid-cols-4' : 'max-w-xl grid-cols-3'}`}>
            <TabsTrigger value="agents" className="gap-2">
              <Users className="w-4 h-4" />
              My Agents
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-2 relative">
              <FileEdit className="w-4 h-4" />
              Drafts
              {drafts.length > 0 && (
                <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem]">
                  {drafts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <Tags className="h-4 w-4" />
              Categories
              {categoryOptions.length > 0 && (
                <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                  {categoryOptions.length}
                </span>
              )}
            </TabsTrigger>
            {hasEnterpriseHierarchy && canCreateAssets && (
              <TabsTrigger value="archived" className="gap-2">
                <Archive className="h-4 w-4" />
                Archived
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {activeTab === 'categories' && (
          <AgentCategoryManager
            canCreate={hasEnterpriseHierarchy ? canCreateAssets : isAdmin}
            canAdminister={hasEnterpriseHierarchy ? isCompanyAdmin || isBranchAdmin : isAdmin}
            onCategoriesChanged={() => setDataRevision((revision) => revision + 1)}
          />
        )}

        {activeTab === 'archived' && hasEnterpriseHierarchy && canCreateAssets && currentTenantId && (
          <ArchivedAgentsPanel
            tenantId={currentTenantId}
            onLifecycleChanged={() => setDataRevision((revision) => revision + 1)}
          />
        )}

        {/* Drafts Tab Content */}
        {activeTab === 'drafts' && (
          <div>
            {draftsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin h-8 w-8 text-primary" />
              </div>
            ) : drafts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {drafts.map((draft) => (
                  <DraftCard 
                    key={draft.id} 
                    draft={draft} 
                    onDelete={handleDeleteDraft}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <FileEdit className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-lg mb-2">No drafts saved</p>
                <p className="text-sm text-muted-foreground mb-4">
                  When you start creating an agent and save a draft, it will appear here.
                </p>
                <Button onClick={() => navigate('/admin/agents/create')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Agent
                </Button>
              </div>
            )}
          </div>
        )}

        {/* My Agents Tab Content */}
        {activeTab === 'agents' && (
          <>
            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="text-sm text-muted-foreground mb-1">Total Agents</div>
                <div className="text-3xl font-bold text-foreground">{stats.total}</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Theater size={14} /> Roleplays
                </div>
                <div className="text-3xl font-bold text-primary">{stats.roleplays}</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <GraduationCap size={14} /> Coaches
                </div>
                <div className="text-3xl font-bold text-accent">{stats.coach}</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <ClipboardCheck size={14} /> Examiners
                </div>
                <div className="text-3xl font-bold text-orange-500">{stats.examiner}</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="text-sm text-muted-foreground mb-1">Active Simulations</div>
                <div className="text-3xl font-bold text-foreground">{stats.activeSimulations}</div>
              </div>
            </div>

            {/* Category Filter Bar */}
            <div className="bg-card border border-border rounded-xl p-4 mb-3">
              <div className="flex flex-col gap-3">
                {/* Primary Category Pills */}
                <div className="flex gap-2 flex-wrap">
                  {(Object.entries(AGENT_CATEGORY_CONFIG) as [AgentCategory, typeof AGENT_CATEGORY_CONFIG[AgentCategory]][]).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setAgentCategory(key);
                        setAgentSubType('all');
                      }}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                        agentCategory === key
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                      }`}
                    >
                      {config.icon}
                      {config.label}
                    </button>
                  ))}
                </div>

                {/* Sub-type Pills */}
                {agentCategory !== 'all' && AGENT_CATEGORY_CONFIG[agentCategory].subTypes.length > 1 && (
                  <div className="flex gap-1.5 flex-wrap pl-2 border-l-2 border-primary/30">
                    {AGENT_CATEGORY_CONFIG[agentCategory].subTypes.map((st) => (
                      <button
                        key={st.value}
                        onClick={() => setAgentSubType(st.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                          agentSubType === st.value
                            ? 'bg-primary/15 text-primary border border-primary/30'
                            : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent'
                        }`}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Search & Additional Filters Bar */}
            <div className="bg-card border border-border rounded-xl p-6 mb-6">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      type="text"
                      placeholder="Search agents by name, specialty, or condition..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                {/* Company Filter - For master tenant admins only */}
                {isAdmin && !isWhiteLabelTenant && (
                  <Select value={filterCompany} onValueChange={setFilterCompany}>
                    <SelectTrigger className="w-full lg:w-[200px]">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      <SelectItem value="global">🌐 Global Only</SelectItem>
                      {allTenants.map((tenantOption) => (
                        <SelectItem key={`tenant-${tenantOption.id}`} value={`tenant:${tenantOption.id}`}>
                          🏢 Tenant: {tenantOption.name}
                        </SelectItem>
                      ))}
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          🏬 {formatCompanyFilterLabel(company)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Agent Source Filter - For white-label tenants (show System + Company agents) */}
                {isWhiteLabelTenant && (
                  <Select value={agentSource} onValueChange={(v) => setAgentSource(v as 'all' | 'system' | 'company')}>
                    <SelectTrigger className="w-full lg:w-[200px]">
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <span className="flex items-center gap-2">
                          <Activity size={14} />
                          All Agents
                        </span>
                      </SelectItem>
                      <SelectItem value="system">
                        <span className="flex items-center gap-2">
                          <Globe size={14} className="text-blue-500" />
                          System Agents
                        </span>
                      </SelectItem>
                      <SelectItem value="company">
                        <span className="flex items-center gap-2">
                          <Building2 size={14} className="text-green-500" />
                          {hasEnterpriseHierarchy ? 'Organisation Agents' : `${userCompany?.name || 'Company'} Agents`}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {hasEnterpriseHierarchy && enterpriseBranches.length > 0 && (
                  <Select value={filterBranch} onValueChange={setFilterBranch}>
                    <SelectTrigger className="w-full lg:w-[220px]">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      <SelectItem value="all-branches">Organisation-wide</SelectItem>
                      {enterpriseBranches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Specialty Filter */}
                <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                  <SelectTrigger className="w-full lg:w-[200px]">
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                    <SelectItem value="pediatrics">Pediatrics</SelectItem>
                    <SelectItem value="surgery">Surgery</SelectItem>
                    <SelectItem value="emergency medicine">Emergency Medicine</SelectItem>
                    <SelectItem value="oncology">Oncology</SelectItem>
                    <SelectItem value="psychiatry">Psychiatry</SelectItem>
                    <SelectItem value="orthopedics">Orthopedics</SelectItem>
                  </SelectContent>
                </Select>

                {/* Category Filter */}
                {categoryOptions.length > 0 && (
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-full lg:w-[220px]">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categoryOptions.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* View Toggle */}
                <div className="flex bg-muted rounded-lg p-1">
                  <button
                    aria-label="Grid view"
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-background shadow' : 'text-muted-foreground'}`}
                  >
                    <Grid3x3 size={20} />
                  </button>
                  <button
                    aria-label="Table view"
                    onClick={() => setViewMode('table')}
                    className={`p-2 rounded ${viewMode === 'table' ? 'bg-background shadow' : 'text-muted-foreground'}`}
                  >
                    <List size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Results count */}
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Showing {filteredAgents.length} agents
              </p>
            </div>

            {/* Grid View */}
            {viewMode === 'grid' && filteredAgents.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} tenants={allTenants} companies={companies} />
                ))}
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && filteredAgents.length > 0 && (
              <AgentTable agents={filteredAgents} />
            )}

            {/* Empty state */}
            {filteredAgents.length === 0 && (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <p className="text-muted-foreground text-lg mb-4">No agents found matching your filters</p>
                <Button variant="outline" onClick={() => {
                  setSearchQuery('');
                  setAgentCategory('all');
                  setAgentSubType('all');
                  setFilterSpecialty('all');
                  setFilterCompany('all');
                  setFilterCategory('all');
                  setFilterBranch('all');
                }}>
                  Clear Filters
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
